import React from 'react';
import { Tag, X, CheckCircle2, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';

/**
 * Reusable coupon code input + applied breakdown card.
 * Pure presentational — all state owned by parent.
 *
 * Props:
 *  - couponInput, setCouponInput
 *  - appliedCoupon: null | { code, discount, original_amount, final_amount, message }
 *  - couponError: string
 *  - couponLoading: bool
 *  - baseAmount: number (current calculated price BEFORE discount)
 *  - onApply, onRemove
 *  - compact?: bool — slightly tighter spacing for narrow modals
 */
export default function CouponBox({
  couponInput, setCouponInput,
  appliedCoupon, couponError, couponLoading,
  baseAmount, onApply, onRemove, compact = false,
}) {
  const padding = compact ? 'p-2.5' : 'p-3';
  const discount = appliedCoupon ? appliedCoupon.discount : 0;
  const finalAmount = appliedCoupon ? appliedCoupon.final_amount : baseAmount;

  if (appliedCoupon) {
    return (
      <div className={`rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 ${padding} shadow-sm`} data-testid="coupon-applied">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center flex-shrink-0">
              <CheckCircle2 size={16} />
            </div>
            <div>
              <div className="text-sm font-bold text-green-800" data-testid="coupon-applied-code">
                {appliedCoupon.code} applied
              </div>
              <div className="text-[11px] text-green-700">You saved ₹{discount.toLocaleString()}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onRemove}
            data-testid="coupon-remove-btn"
            className="text-gray-500 hover:text-red-600 transition-colors p-1"
            aria-label="Remove coupon"
          >
            <X size={16} />
          </button>
        </div>
        {/* Breakdown */}
        <div className="bg-white rounded-lg p-2.5 space-y-1 border border-green-100">
          <div className="flex justify-between text-xs text-gray-600">
            <span>Original Amount</span>
            <span data-testid="coupon-original">₹{appliedCoupon.original_amount.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-xs text-green-700 font-semibold">
            <span>Discount</span>
            <span data-testid="coupon-discount">− ₹{discount.toLocaleString()}</span>
          </div>
          <div className="flex justify-between pt-1.5 mt-1 border-t border-dashed border-green-200 text-sm font-bold text-gray-900">
            <span>Payable</span>
            <span className="text-green-700" data-testid="coupon-final">₹{finalAmount.toLocaleString()}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border-2 border-dashed border-green-200 bg-green-50/50 ${padding}`} data-testid="coupon-section">
      <div className="flex items-center gap-2 mb-2">
        <Tag size={14} className="text-green-700" />
        <Label className="text-sm font-semibold text-gray-800">Have a coupon code?</Label>
      </div>
      <div className="flex gap-2">
        <Input
          value={couponInput}
          onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onApply(); } }}
          placeholder="ENTER CODE"
          className="h-10 uppercase tracking-wider font-semibold text-sm bg-white text-gray-900 placeholder:text-gray-400"
          maxLength={24}
          data-testid="coupon-input"
        />
        <Button
          onClick={onApply}
          disabled={couponLoading || !couponInput.trim() || baseAmount <= 0}
          data-testid="coupon-apply-btn"
          className="h-10 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg whitespace-nowrap"
        >
          {couponLoading ? <Loader2 size={14} className="animate-spin" /> : 'Apply'}
        </Button>
      </div>
      {couponError && (
        <p className="mt-2 text-xs text-red-600 flex items-start gap-1" data-testid="coupon-error">
          <X size={13} className="flex-shrink-0 mt-0.5" /> {couponError}
        </p>
      )}
      {baseAmount <= 0 && !couponError && (
        <p className="mt-2 text-[11px] text-gray-500">Select pest &amp; property size first to apply a coupon.</p>
      )}
    </div>
  );
}
